import React, { useCallback, useState, useRef } from "react";
import { Form, InputNumber, Switch, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Badge, Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import SearchSelect from "../../components/SearchSelect";
import FilterBar from "../../components/FilterBar";

const SubscriptionList = () => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (page, pageSize, search, f) => {
      try {
        const { data } = await mutate("fetchSubscription", {
          method: "get",
          endpoint: "/subscription",
          params: {
            page,
            pageSize,
            search,
            status: f?.status,
          },
        });
        return {
          data: data?.data?.items || [],
          total: data?.data?.totalCount || 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },
    [mutate]
  );

  const columns = [
    { title: "Company", dataIndex: ["company", "name"], key: "company" },
    {
      title: "Package",
      key: "package",
      render: (_, record) =>
        record.packageNameSnapshot || record.package?.name || "-",
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Auto Renew",
      dataIndex: "autoRenew",
      key: "autoRenew",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      // Unsettled overdraft, surfaced in the list so an operator sees who is behind — and who
      // is outright blocked — without opening each subscription.
      title: "Owed",
      key: "owed",
      render: (_, record) => {
        const owed = record.overdraftOutstanding || 0;
        if (owed <= 0) return <span style={{ color: "var(--text-muted)" }}>—</span>;

        return (
          <Tooltip
            title={`Due ${new Date(record.overdraftDueAtUtc).toLocaleDateString()}${
              record.overdraftIsOverdue ? " — API blocked until settled" : ""
            }`}
          >
            <Badge variant={record.overdraftIsOverdue ? "error" : "warning"}>
              {owed.toLocaleString()} tokens
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Tooltip title="Details">
            <Button
              variant="ghost"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(`/subscription/${record.subscriptionId}`)
              }
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              variant="ghost"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Archive">
            <Button
              variant="ghost"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                confirmAction({
                  key: "deleteSubscription",
                  method: "delete",
                  endpoint: `/subscription/${record.subscriptionId}`,
                  title: "Archive Subscription",
                  content:
                    "Archive this subscription? Historical payments and invoices stay intact, while API access is disabled.",
                  okText: "Archive",
                  okType: "danger",
                  onSuccess: () => tableRef.current?.reloadPage(),
                })
              }
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, autoRenew: true });
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    if (editing) {
      await mutate("updateSubscription", {
        method: "put",
        endpoint: `/subscription/${editing.subscriptionId}`,
        payload: values,
      });
    } else {
      await mutate("createSubscription", {
        method: "post",
        endpoint: "/subscription",
        payload: values,
      });
    }
    tableRef.current?.reloadPage();
    setOpen(false);
  };

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        subtitle="Active plans linking clients to their packages and billing cycles."
        extra={
          <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
            Create Subscription
          </Button>
        }
      />
      <FilterBar filters={filters} onChange={setFilters} showStatus />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        filters={filters}
        rowKey="subscriptionId"
      />
      <Modal
        title={editing ? "Edit Subscription" : "Create Subscription"}
        open={open}
        onConfirm={handleOk}
        onClose={() => setOpen(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="subscriptionId" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="companyId"
            label="Company"
            rules={[{ required: true }]}
            hidden={!!editing}
          >
            <SearchSelect
              fetchKey="searchClients"
              endpoint="/clients/search"
              labelKey="name"
              valueKey="companyId"
              placeholder="Select company"
            />
          </Form.Item>
          <Form.Item
            name="packageId"
            label="Package"
            rules={[{ required: true }]}
            hidden={!!editing}
          >
            <SearchSelect
              fetchKey="searchPackages"
              endpoint="/packages/search"
              labelKey="name"
              valueKey="packageId"
              placeholder="Select package"
            />
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="autoRenew"
            label="Auto Renew"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionList;
